import { useState, useEffect, useRef } from 'react';
import { chatApi, type ChatAction } from '../api/chat';
import { PageHeader } from '../components/ui/PageHeader';

// ADR 018 — native chat in the PWA. Deterministic bot (regex parsing, no LLM).
// Conversation history is ephemeral (in memory only, per ADR).

interface ChatMessage {
  from: 'user' | 'bot';
  text: string;
}

const ACTION_LABEL: Record<ChatAction, (item?: string) => string> = {
  picked:      (item) => `✅ ${item ?? 'Item'} marcado como pego`,
  skipped:     (item) => `➡️ ${item ?? 'Item'} pulado`,
  not_found:   (item) => `❌ ${item ?? 'Item'} marcado como não encontrado`,
  added:       (item) => `➕ ${item ?? 'Item'} adicionado à compra`,
  price_set:   (item) => `💰 Preço registrado: ${item ?? 'item'}`,
};

const SUGGESTIONS = ['peguei arroz', 'arroz 21,90', 'pulei detergente', 'não encontrei leite', 'adicionar papel toalha'];

export function ChatScreen() {
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      from: 'bot',
      text: 'Oi! Durante a compra, escreva o que você fez:\n• "peguei arroz"\n• "arroz 21,90" (registra o preço)\n• "pulei feijão" / "não encontrei leite"\n• "adicionar biscoito"',
    },
  ]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const listEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    listEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const send = async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || sending) return;
    setInput('');
    setMessages((m) => [...m, { from: 'user', text: trimmed }]);
    setSending(true);
    const result = await chatApi.send(trimmed);
    const botText = result.success
      ? (ACTION_LABEL[result.action](result.item))
      : result.code === 'NO_ACTIVE_SESSION'
        ? `Não há compra em andamento. Inicie uma na aba "Compra".`
        : `${result.error}`;
    setMessages((m) => [...m, { from: 'bot', text: botText }]);
    setSending(false);
  };

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <PageHeader title="Chat da compra" showBack />

      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
        {messages.map((msg, i) => (
          <div
            key={i}
            className={`flex ${msg.from === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={[
                'max-w-[80%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-line',
                msg.from === 'user'
                  ? 'bg-emerald-500 text-white rounded-br-md'
                  : 'bg-white border border-gray-200 text-gray-800 rounded-bl-md shadow-sm',
              ].join(' ')}
            >
              {msg.text}
            </div>
          </div>
        ))}
        {sending && (
          <div className="flex justify-start">
            <div className="bg-white border border-gray-200 rounded-2xl rounded-bl-md px-4 py-2.5">
              <div className="flex gap-1">
                <span className="w-2 h-2 bg-gray-300 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-2 h-2 bg-gray-300 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-2 h-2 bg-gray-300 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}
        <div ref={listEndRef} />
      </div>

      <div
        className="bg-white border-t border-gray-200 px-3 py-2.5 space-y-2"
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 10px)' }}
      >
        <div className="flex gap-2 overflow-x-auto pb-1">
          {SUGGESTIONS.map((s) => (
            <button
              key={s}
              onClick={() => send(s)}
              disabled={sending}
              className="flex-shrink-0 px-3 py-1.5 rounded-full border border-gray-300 text-xs text-gray-600 hover:bg-gray-50 touch-manipulation disabled:opacity-50"
            >
              {s}
            </button>
          ))}
        </div>
        <form
          className="flex gap-2"
          onSubmit={(e) => { e.preventDefault(); send(input); }}
        >
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder='ex: "peguei arroz"'
            disabled={sending}
            className="flex-1 h-12 px-4 rounded-xl border border-gray-300 text-base focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-50"
            aria-label="Mensagem"
          />
          <button
            type="submit"
            disabled={sending || !input.trim()}
            className="w-12 h-12 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl flex items-center justify-center touch-manipulation disabled:opacity-50"
            aria-label="Enviar"
          >
            <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />
            </svg>
          </button>
        </form>
      </div>
    </div>
  );
}
